/**
 * Export API client functions.
 *
 * These functions handle data export operations that trigger browser file downloads.
 * All exports are authenticated and use the current user's session.
 */

import { formatDateISO } from '@/utils/date';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Extracts filename from Content-Disposition header.
 * Falls back to default filename if header is missing or malformed.
 */
function extractFilename(headers: Headers, defaultFilename: string): string {
    const disposition = headers.get('content-disposition');
    if (!disposition) {
        return defaultFilename;
    }

    // Try to extract filename from: attachment; filename="example.csv"
    const filenameMatch = disposition.match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/i);
    return filenameMatch?.[1] || defaultFilename;
}

/**
 * Triggers a browser download for a blob with the specified filename.
 */
function triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Generic fetch function for file downloads.
 * Handles authentication and triggers browser download.
 */
async function fetchFileDownload(endpoint: string, defaultFilename: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        credentials: 'include',
    });

    if (!response.ok) {
        if (response.status === 401) {
            window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }
        throw new Error(`Export failed: ${response.status}`);
    }

    const blob = await response.blob();
    const filename = extractFilename(response.headers, defaultFilename);
    triggerDownload(blob, filename);
}

/**
 * Export full account data as ZIP file.
 *
 * Downloads a ZIP file containing:
 * - manifest.json (version, export date, user email, entity counts)
 * - collections.csv
 * - tags.csv
 * - ledgers.csv
 * - values.csv
 * - returns.csv
 *
 * @throws Error if the export fails
 */
export async function exportFullZip(): Promise<void> {
    const today = formatDateISO();
    await fetchFileDownload('/api/export/zip', `nf-export-${today}.zip`);
}

/**
 * Export values for a specific ledger as CSV file.
 *
 * CSV format includes:
 * - Comment header with ledger metadata
 * - Headers: Date, Value, Currency, Description
 * - Values in decimal format (converted from minor units)
 *
 * @param ledgerId - The ID of the ledger to export values from
 * @throws Error if the export fails or ledger is not found
 */
export async function exportLedgerValues(ledgerId: number): Promise<void> {
    const today = formatDateISO();
    await fetchFileDownload(
        `/api/ledgers/${ledgerId}/values/export`,
        `ledger-${ledgerId}-values-${today}.csv`
    );
}

/**
 * Export returns (cash flows) for a specific ledger as CSV file.
 *
 * CSV format includes:
 * - Comment header with ledger metadata
 * - Headers: Date, Type, Value, Currency, Label
 * - Values in decimal format (converted from minor units)
 *
 * @param ledgerId - The ID of the ledger to export returns from
 * @throws Error if the export fails or ledger is not found
 */
export async function exportLedgerReturns(ledgerId: number): Promise<void> {
    const today = formatDateISO();
    await fetchFileDownload(
        `/api/ledgers/${ledgerId}/returns/export`,
        `ledger-${ledgerId}-cashflow-${today}.csv`
    );
}

/**
 * Download the values import template CSV file.
 *
 * Template includes example rows with realistic data:
 * - Date (YYYY-MM-DD format)
 * - Value (decimal amount)
 * - Currency (ISO code)
 * - Description (optional)
 */
export async function downloadValuesTemplate(): Promise<void> {
    await fetchFileDownload('/api/import/templates/values', 'values-template.csv');
}

/**
 * Download the returns import template CSV file.
 *
 * Template includes example rows with realistic data:
 * - Date (YYYY-MM-DD format)
 * - Type (inflow or outflow)
 * - Value (decimal amount)
 * - Currency (ISO code)
 * - Label (optional description)
 */
export async function downloadReturnsTemplate(): Promise<void> {
    await fetchFileDownload('/api/import/templates/returns', 'returns-template.csv');
}
